import { useState, useEffect } from "react";
import { Clock, Trash2 } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

interface SessionRangeItemProps {
  sess: any;
  onUpdate: (id: number, data: { start_time?: string; end_time?: string }) => Promise<void>;
  onDelete: (id: number) => void;
  lang: string;
}

export function SessionRangeItem({ sess, onUpdate, onDelete, lang }: SessionRangeItemProps) {
  const calculateEndTime = (start: string, duration: number) => {
    const [h, m] = start.split(":").map(Number);
    const date = new Date();
    date.setHours(h, m + duration);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const [localStart, setLocalStart] = useState(sess.start_time);
  const [localEnd, setLocalEnd] = useState(calculateEndTime(sess.start_time, sess.duration_minutes));
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setLocalStart(sess.start_time);
    setLocalEnd(calculateEndTime(sess.start_time, sess.duration_minutes));
    setIsError(false);
  }, [sess]);

  const validateAndSave = async () => {
    const [h1, m1] = localStart.split(":").map(Number);
    const [h2, m2] = localEnd.split(":").map(Number);
    const duration = h2 * 60 + m2 - (h1 * 60 + m1);

    if (duration <= 0) {
      setIsError(true);
      return;
    }

    setIsError(false);
    if (localStart !== sess.start_time || duration !== sess.duration_minutes) {
      await onUpdate(sess.id, { start_time: localStart, end_time: localEnd });
    }
  };

  return (
    <div className="flex items-center justify-between p-2 pl-4 rounded-2xl bg-neutral-50/50 border border-neutral-100 group transition-all hover:bg-white">
      <div className="flex items-center gap-4 flex-1">
        <div className="flex items-center gap-3 flex-1">
          <Input
            type="time"
            value={localStart}
            className={cn(
              "h-11 px-4 text-xs font-bold uppercase tracking-widest bg-white border-neutral-100 rounded-xl transition-all focus-visible:ring-emerald-500/20 focus:border-emerald-200 shadow-sm",
              isError && "border-red-500 ring-2 ring-red-500/20"
            )}
            onChange={(e) => {
              setLocalStart(e.target.value);
              setIsError(false);
            }}
            onBlur={validateAndSave}
          />
          <div className="w-4 h-0.5 bg-neutral-200 rounded-full" />
          <Input
            type="time"
            value={localEnd}
            className={cn(
              "h-11 px-4 text-xs font-bold uppercase tracking-widest bg-white border-neutral-100 rounded-xl transition-all focus-visible:ring-emerald-500/20 focus:border-emerald-200 shadow-sm",
              isError && "border-red-500 ring-2 ring-red-500/20"
            )}
            onChange={(e) => {
              setLocalEnd(e.target.value);
              setIsError(false);
            }}
            onBlur={validateAndSave}
          />
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="h-10 w-10 rounded-xl ml-2 transition-all hover:bg-red-50 hover:text-red-600 active:scale-90"
        onClick={() => onDelete(sess.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
